<template>
  <div class="job-profile-side-revision h-100 overflow-y-auto">
    <div
      v-if="sideRevisionLoading"
      class="d-flex justify-center align-center h-100"
    >
      <v-progress-circular
        :size="32"
        :width="3"
        color="primary"
        indeterminate
      />
    </div>

    <div
      v-else-if="sideRevision"
      class="pa-6 d-flex flex-column ga-2"
    >
      <div class="d-flex align-center ga-4">
        <v-btn
          icon="mdi-chevron-left"
          variant="text"
          size="small"
          :disabled="!prevRevision"
          @click="navigate(prevRevision)"
        />
        <RevisionListItem
          :revision="{ ...revisions[currentIndex], index: currentIndex }"
        />
        <v-spacer />
        <v-btn
          icon="mdi-chevron-right"
          variant="text"
          size="small"
          :disabled="!nextRevision"
          @click="navigate(nextRevision)"
        />
      </div>

      <div class="text-break py-2">
        <div class="text-h5 font-weight-medium">
          <DpplSafeHtml :html="getHTMLDiffCombined(sideRevision.title, jobProfile.title)" />
        </div>
      </div>

      <div
        class="text-body-2 text-medium-emphasis"
      >
        {{ sideRevision.location }} - {{ sideRevision.job_code }} - {{ sideRevision.job_level }}
      </div>

      <div
        v-for="field in revisionFields"
        :key="field.type"
        class="py-2"
      >
        <div class="d-flex justify-end mt-2 mb-1">
          <FieldLabel>{{ field.label }}</FieldLabel>
        </div>

        <div class="field-content">
          <div
            v-if="field.headerHtml && !isEmptyHtml(field.headerHtml)"
            class="locked-field"
          >
            <DpplSafeHtml
              :html="field.headerHtml"
            />
          </div>
          <DpplSafeHtml
            v-if="field.html"
            :html="field.html"
          />
          <div
            v-if="field.footerHtml && !isEmptyHtml(field.footerHtml)"
            class="locked-field"
          >
            <DpplSafeHtml
              :html="field.footerHtml"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useAppRoute } from '@/stores/app/route';
import FieldLabel from '@/components/editor/field/FieldLabel.vue';
import DpplSafeHtml from '@ui/components/general/DpplSafeHtml/DpplSafeHtml.vue';
import { getHTMLDiffCombined, joinAndDiffBlocks } from '@/helpers/diff';
import { isEmptyHtml } from '@ui/helpers/htmlToText';
import RevisionListItem from '@ui/components/editor/RevisionList/RevisionListItem.vue';

const {
  sideRevision, sideRevisionLoading, jobProfile, revisions,
} = storeToRefs(useCurrentJobProfile());
const { route } = storeToRefs(useAppRoute());
const router = useRouter();

const currentIndex = computed(() => revisions.value.findIndex((r) => r.id === route.value?.query?.sideRevision));
const prevRevision = computed(() => revisions.value[currentIndex.value - 1] ?? null);
const nextRevision = computed(() => revisions.value[currentIndex.value + 1] ?? null);

const navigate = (revision) => {
  router.push({ query: { ...route.value?.query, sideRevision: revision.id } });
};

const revisionFields = computed(() => (sideRevision.value?.fields || []).map((field, index) => ({
  ...field,
  html: joinAndDiffBlocks(field.blocks || [], jobProfile.value.fields[index].blocks || []),
  headerHtml: joinAndDiffBlocks(field.header || [], jobProfile.value.fields[index].header || []),
  footerHtml: joinAndDiffBlocks(field.footer || [], jobProfile.value.fields[index].footer || []),
})));
</script>

<style scoped lang="less">
.job-profile-side-revision {
  border-right: 1px solid @stroke-default;
  :deep(.removed-diff) {
    background: @red-200;
    text-decoration: line-through;
  }
  :deep(.added-diff) {
    background: @green-200;
  }
}
.field-content {
  & {
    :deep(h1), :deep(h2), :deep(h3) {
      line-height: 28px;
      font-weight: 700;
      padding: 8px 40px 8px 0px;
    }
    :deep(h1) {
      font-size: 24px;
    }
    :deep(h2) {
      font-size: 20px;
    }
    :deep(h3) {
      font-size: 18px;
    }
    :deep(p), :deep(section), :deep(article) {
      font-size: 16px;
      line-height: 28px;
      font-weight: 300;
      padding: 8px 40px 8px 0px;
    }
    :deep(span) {
      font-size: 16px;
      line-height: 28px;
      font-weight: 300;
    }
    :deep(strong), :deep(b) {
      font-weight: 700;
    }
    :deep(ul), :deep(ol) {
      margin-left: 16px;
      padding: 0;

      li {
        padding-right: 0;
        font-size: 16px;
        line-height: 28px;
        font-weight: 300;
        max-width: 524px;
      }
    }
    :deep(ul) li::marker {
      unicode-bidi: isolate;
      font-variant-numeric: tabular-nums;
      text-transform: none;
      text-indent: 0px !important;
      text-align: start !important;
      text-align-last: start !important;
    }
  }
}
.locked-field {
  background-color: fade(@bg-brand-default, 5%);
}
</style>
